/* =========================================================
   File: assets/sh-product-flooring.js
   Website: Capital Floorings
   Purpose:
   - Product gallery
   - Flooring calculator
   - Variant picker / swatches
   - Safe product JSON handling
   - Add to cart redirect fix
   - Order sample button
   - Copy / print / accordion helpers
========================================================= */

(function () {
  'use strict';

  function formatMoney(value) {
    var number = Number(value || 0);

    if (Number.isNaN(number)) {
      number = 0;
    }

    /*
      Shopify variant.price is usually in cents from Liquid JSON.
      If a metafield price comes as normal decimal, keep it as-is.
    */
    if (number > 999) {
      number = number / 100;
    }

    return '$' + number.toFixed(2);
  }

  function parseDecimal(value, fallback) {
    var number = Number(String(value || '').replace(/,/g, ''));

    if (Number.isNaN(number)) {
      return fallback || 0;
    }

    return number;
  }

  function getProductData(scope) {
    var jsonEl = null;

    if (scope) {
      jsonEl =
        scope.querySelector('[data-product-json]') ||
        scope.querySelector('[data-sh-product-json]');
    }

    if (!jsonEl) {
      jsonEl =
        document.querySelector('[data-product-json]') ||
        document.querySelector('[data-sh-product-json]');
    }

    if (!jsonEl) {
      return null;
    }

    try {
      return JSON.parse(jsonEl.textContent.trim());
    } catch (error) {
      console.warn('SH Product JSON parse error:', error);
      return null;
    }
  }

  function getVariantById(productData, id) {
    if (!productData || !Array.isArray(productData.variants) || !id) {
      return null;
    }

    return (
      productData.variants.find(function (variant) {
        return variant && String(variant.id) === String(id);
      }) || null
    );
  }

  function getSelectedOptions(scope) {
    var selectedOptions = [];

    var optionInputs = Array.prototype.slice.call(
      scope.querySelectorAll('[data-variant-option-input]')
    );

    if (!optionInputs.length) {
      optionInputs = Array.prototype.slice.call(
        scope.querySelectorAll(
          'select[data-option-position], input[type="radio"][data-option-position]'
        )
      );
    }

    optionInputs.forEach(function (element) {
      var position = Number(element.getAttribute('data-option-position'));

      if (!position) {
        return;
      }

      if (element.matches('select')) {
        selectedOptions[position - 1] = element.value;
        return;
      }

      if (element.matches('input[type="radio"]') && element.checked) {
        selectedOptions[position - 1] = element.value;
      }
    });

    return selectedOptions;
  }

  function findVariantByOptions(productData, selectedOptions) {
    if (
      !productData ||
      !Array.isArray(productData.variants) ||
      !Array.isArray(selectedOptions) ||
      !selectedOptions.length
    ) {
      return null;
    }

    return (
      productData.variants.find(function (variant) {
        if (!variant || !Array.isArray(variant.options)) {
          return false;
        }

        if (variant.options.length !== selectedOptions.length) {
          return false;
        }

        return variant.options.every(function (option, index) {
          return String(option) === String(selectedOptions[index]);
        });
      }) || null
    );
  }

  function initProductGallery(scope) {
    var gallery = scope.querySelector('[data-product-gallery]');

    if (!gallery || gallery.dataset.initialized === 'true') {
      return;
    }

    gallery.dataset.initialized = 'true';

    var mainImage = gallery.querySelector('[data-gallery-main-image]');
    var thumbs = Array.prototype.slice.call(gallery.querySelectorAll('[data-gallery-thumb]'));
    var prevButton = gallery.querySelector('[data-gallery-prev]');
    var nextButton = gallery.querySelector('[data-gallery-next]');

    if (!mainImage || !thumbs.length) {
      return;
    }

    var activeIndex = thumbs.findIndex(function (thumb) {
      return thumb.classList.contains('is-active');
    });

    if (activeIndex < 0) {
      activeIndex = 0;
    }

    function setActiveImage(index) {
      if (!thumbs[index]) {
        return;
      }

      activeIndex = index;

      var activeThumb = thumbs[activeIndex];
      var imageSrc = activeThumb.dataset.imageSrc;
      var imageAlt = activeThumb.dataset.imageAlt || '';

      thumbs.forEach(function (thumb) {
        thumb.classList.remove('is-active');
      });

      activeThumb.classList.add('is-active');

      if (!imageSrc) {
        return;
      }

      mainImage.style.opacity = '0';

      window.setTimeout(function () {
        mainImage.src = imageSrc;
        mainImage.alt = imageAlt;
        mainImage.style.opacity = '1';
      }, 140);
    }

    function setImageBySrc(imageSrc) {
      if (!imageSrc) {
        return;
      }

      var matchingIndex = thumbs.findIndex(function (thumb) {
        return thumb.dataset.imageSrc === imageSrc;
      });

      if (matchingIndex >= 0) {
        setActiveImage(matchingIndex);
        return;
      }

      mainImage.style.opacity = '0';

      window.setTimeout(function () {
        mainImage.src = imageSrc;
        mainImage.style.opacity = '1';
      }, 140);
    }

    gallery.shSetImageBySrc = setImageBySrc;

    thumbs.forEach(function (thumb, index) {
      thumb.addEventListener('click', function () {
        setActiveImage(index);
      });
    });

    if (prevButton) {
      prevButton.addEventListener('click', function () {
        setActiveImage(activeIndex - 1 < 0 ? thumbs.length - 1 : activeIndex - 1);
      });
    }

    if (nextButton) {
      nextButton.addEventListener('click', function () {
        setActiveImage(activeIndex + 1 >= thumbs.length ? 0 : activeIndex + 1);
      });
    }
  }

  function initFlooringCalculator(scope) {
    var calculator = scope.querySelector('.sh-flooring-calculator');

    if (!calculator || calculator.dataset.initialized === 'true') {
      return;
    }

    calculator.dataset.initialized = 'true';

    var toggle = calculator.querySelector('[data-calculator-toggle]');
    var toggleText = calculator.querySelector('[data-calculator-toggle-text]');
    var advanced = calculator.querySelector('[data-calculator-advanced]');
    var lengthInput = calculator.querySelector('[data-length-input]');
    var widthInput = calculator.querySelector('[data-width-input]');
    var projectInput = calculator.querySelector('[data-project-size-input]');
    var qtyInput = calculator.querySelector('[data-qty-input]');
    var minusBtn = calculator.querySelector('[data-qty-minus]');
    var plusBtn = calculator.querySelector('[data-qty-plus]');
    var contingencyInput = calculator.querySelector('[data-contingency-input]');
    var subtotalOutput = calculator.querySelector('[data-subtotal-output]');

    var openText = calculator.dataset.openText || 'Calculate Square Footage';
    var closeText = calculator.dataset.closeText || 'Hide Calculator';
    var isAdvancedOpen = false;

    function getPricePerBox() {
      return parseDecimal(calculator.dataset.pricePerBox, 0);
    }

    function getSqftPerBox() {
      var number = parseDecimal(calculator.dataset.sqftPerBox, 1);
      return number <= 0 ? 1 : number;
    }

    function getContingency() {
      var number = parseDecimal(calculator.dataset.contingency, 10);
      return number < 0 ? 10 : number;
    }

    function getCurrentQuantity() {
      if (!qtyInput) {
        return 1;
      }

      var quantity = Number(qtyInput.value || 1);

      if (Number.isNaN(quantity) || quantity < 1) {
        return 1;
      }

      return quantity;
    }

    function syncMainQuantity(quantity) {
      var formQtyInput = scope.querySelector(
        '.sh-product-form input[name="quantity"]:not([data-qty-input])'
      );

      if (formQtyInput) {
        formQtyInput.value = quantity;
      }
    }

    function setQuantity(quantity) {
      quantity = Math.max(1, Math.ceil(Number(quantity || 1)));

      if (Number.isNaN(quantity)) {
        quantity = 1;
      }

      if (qtyInput) {
        qtyInput.value = quantity;
      }

      syncMainQuantity(quantity);
      updateSubtotal();
    }

    function updateSubtotal() {
      var quantity = getCurrentQuantity();
      var subtotal = quantity * getPricePerBox();

      syncMainQuantity(quantity);

      if (subtotalOutput) {
        subtotalOutput.textContent = formatMoney(subtotal);
      }
    }

    function calculateFromSqft() {
      if (!projectInput) {
        return;
      }

      var projectSqft = Number(projectInput.value || 0);

      if (Number.isNaN(projectSqft)) {
        projectSqft = 0;
      }

      if (contingencyInput && contingencyInput.checked) {
        projectSqft = projectSqft * (1 + getContingency() / 100);
      }

      if (projectSqft > 0 && getSqftPerBox() > 0) {
        setQuantity(Math.ceil(projectSqft / getSqftPerBox()));
      } else {
        updateSubtotal();
      }
    }

    function calculateFromLengthWidth() {
      if (!lengthInput || !widthInput || !projectInput) {
        return;
      }

      var length = Number(lengthInput.value || 0);
      var width = Number(widthInput.value || 0);

      if (Number.isNaN(length)) {
        length = 0;
      }

      if (Number.isNaN(width)) {
        width = 0;
      }

      if (length > 0 && width > 0) {
        projectInput.value = (length * width).toFixed(2);
        calculateFromSqft();
      }
    }

    calculator.shRecalculate = function () {
      if (projectInput && Number(projectInput.value || 0) > 0) {
        calculateFromSqft();
      } else {
        updateSubtotal();
      }
    };

    if (toggle && advanced) {
      toggle.addEventListener('click', function () {
        isAdvancedOpen = !isAdvancedOpen;
        advanced.hidden = !isAdvancedOpen;

        if (toggleText) {
          toggleText.textContent = isAdvancedOpen ? closeText : openText;
        }
      });
    }

    if (projectInput) {
      projectInput.addEventListener('input', calculateFromSqft);
    }

    if (lengthInput) {
      lengthInput.addEventListener('input', calculateFromLengthWidth);
    }

    if (widthInput) {
      widthInput.addEventListener('input', calculateFromLengthWidth);
    }

    if (contingencyInput) {
      contingencyInput.addEventListener('change', calculateFromSqft);
    }

    if (minusBtn) {
      minusBtn.addEventListener('click', function () {
        setQuantity(getCurrentQuantity() - 1);
      });
    }

    if (plusBtn) {
      plusBtn.addEventListener('click', function () {
        setQuantity(getCurrentQuantity() + 1);
      });
    }

    if (qtyInput) {
      qtyInput.addEventListener('input', updateSubtotal);

      qtyInput.addEventListener('change', function () {
        setQuantity(qtyInput.value);
      });
    }

    updateSubtotal();
  }

  function updateSelectedOptionLabels(scope) {
    var optionInputs = Array.prototype.slice.call(
      scope.querySelectorAll('[data-variant-option-input]')
    );

    optionInputs.forEach(function (input) {
      var position = input.getAttribute('data-option-position');

      if (!position) {
        return;
      }

      if (input.matches('input[type="radio"]') && !input.checked) {
        return;
      }

      var label = scope.querySelector('[data-selected-option-label="' + position + '"]');

      if (label) {
        label.textContent = input.value;
      }
    });
  }

  function updateSwatchActiveStates(scope) {
    Array.prototype.slice.call(scope.querySelectorAll('[data-color-swatches]')).forEach(function (group) {
      Array.prototype.slice.call(group.querySelectorAll('.sh-product-color-swatch')).forEach(function (swatch) {
        var input = swatch.querySelector('input[type="radio"]');
        swatch.classList.toggle('is-active', !!(input && input.checked));
      });
    });

    Array.prototype.slice.call(scope.querySelectorAll('[data-size-swatches]')).forEach(function (group) {
      var position = group.getAttribute('data-option-position');
      var select = scope.querySelector(
        'select[data-option-position="' + position + '"]'
      );

      Array.prototype.slice.call(group.querySelectorAll('.sh-size-swatch')).forEach(function (button) {
        var isActive = select && String(select.value) === String(button.getAttribute('data-size-value'));
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
    });
  }

  function updateProductForVariant(scope, variant) {
    var variantIdInput = scope.querySelector(
      'input[name="id"][data-variant-id-input], input[name="id"][data-normal-product-variant-id], input[name="id"][data-product-variant-id], input[name="id"]'
    );

    var skuRow = scope.querySelector('[data-sku-row]');
    var skuOutput = scope.querySelector('[data-sku-output]');
    var priceMain = scope.querySelector('[data-price-main]');
    var priceBox = scope.querySelector('[data-price-box]');
    var sizeRow = scope.querySelector('[data-product-size-row]');
    var sizeOutput = scope.querySelector('[data-product-size]');
    var calculator = scope.querySelector('.sh-flooring-calculator');
    var boxPriceOutput = scope.querySelector('[data-box-price-output]');
    var boxSqftOutput = scope.querySelector('[data-box-sqft-output]');
    var addButton = scope.querySelector('[data-add-to-cart-button]');
    var addButtonText = scope.querySelector('[data-add-to-cart-text]');
    var sampleButton = scope.querySelector('[data-order-sample-button]');
    var gallery = scope.querySelector('[data-product-gallery]');

    if (!variant) {
      if (variantIdInput) {
        variantIdInput.value = '';
      }

      if (addButton) {
        addButton.disabled = true;
        addButton.setAttribute('aria-disabled', 'true');
      }

      if (addButtonText) {
        addButtonText.textContent = 'Select Option';
      }

      return;
    }

    if (variantIdInput) {
      variantIdInput.value = variant.id;
      variantIdInput.setAttribute('value', variant.id);
    }

    if (skuRow && skuOutput) {
      skuRow.hidden = !variant.sku;
      skuOutput.textContent = variant.sku || '';
    }

    if (priceMain) {
      priceMain.innerHTML = formatMoney(variant.price) + ' <span>/ item</span>';
    }

    if (priceBox) {
      priceBox.hidden = !variant.pricePerBox;
      priceBox.textContent = variant.pricePerBox ? '$' + variant.pricePerBox + ' / box' : '';
    }

    if (sizeRow && sizeOutput) {
      sizeRow.hidden = !variant.sizeLabel;
      sizeOutput.textContent = variant.sizeLabel || '';
    }

    if (calculator) {
      calculator.dataset.pricePerBox = variant.pricePerBox || variant.price || 0;
      calculator.dataset.sqftPerBox = variant.sqftPerBox || 1;

      if (calculator.shRecalculate) {
        calculator.shRecalculate();
      }
    }

    if (boxPriceOutput) {
      boxPriceOutput.textContent = variant.pricePerBox
        ? '$' + variant.pricePerBox + ' / box'
        : formatMoney(variant.price) + ' / item';
    }

    if (boxSqftOutput) {
      boxSqftOutput.textContent = variant.sqftPerBox || 1;
    }

    if (addButton && addButtonText) {
      addButton.disabled = !variant.available;

      if (variant.available) {
        addButton.removeAttribute('aria-disabled');
      } else {
        addButton.setAttribute('aria-disabled', 'true');
      }

      addButtonText.textContent = variant.available ? 'Add To Cart' : 'Sold Out';
    }

    if (sampleButton) {
      sampleButton.setAttribute('data-current-product-variant-id', variant.id);
      sampleButton.setAttribute('data-sample-variant-title', variant.title || '');
    }

    if (gallery && gallery.shSetImageBySrc && variant.featuredImage) {
      gallery.shSetImageBySrc(variant.featuredImage);
    }

    document.dispatchEvent(
      new CustomEvent('sh:variant-change', {
        detail: {
          variant: variant,
          scope: scope
        }
      })
    );
  }

  function initVariantPicker(scope) {
    var picker = scope.querySelector('[data-variant-picker]');
    var productData = getProductData(scope);

    if (!picker || !productData || picker.dataset.initialized === 'true') {
      return;
    }

    picker.dataset.initialized = 'true';

    function updateCurrentVariant() {
      var selectedOptions = getSelectedOptions(scope);
      var variant = findVariantByOptions(productData, selectedOptions);

      updateSelectedOptionLabels(scope);
      updateSwatchActiveStates(scope);
      updateProductForVariant(scope, variant);
    }

    picker.addEventListener('change', function (event) {
      var target = event.target;

      if (target.matches('input[type="radio"][data-option-position]')) {
        target.checked = true;
      }

      updateCurrentVariant();
    });

    picker.addEventListener('click', function (event) {
      var sizeButton = event.target.closest('.sh-size-swatch');

      if (!sizeButton) {
        return;
      }

      event.preventDefault();

      var optionPosition = sizeButton.getAttribute('data-option-position');
      var value = sizeButton.getAttribute('data-size-value');

      var select = picker.querySelector(
        'select[data-option-position="' + optionPosition + '"]'
      );

      if (!select) {
        return;
      }

      select.value = value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    });

    updateCurrentVariant();
  }

  function ajaxAddAndRedirect(form, button, buttonText) {
    var formData = new FormData(form);

    if (!formData.get('quantity')) {
      formData.set('quantity', '1');
    }

    if (button) {
      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
    }

    if (buttonText) {
      buttonText.textContent = 'Adding...';
    }

    return fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        Accept: 'application/json'
      },
      body: formData
    })
      .then(function (response) {
        if (!response.ok) {
          return response.json().then(function (data) {
            throw data;
          });
        }

        return response.json();
      })
      .then(function () {
        window.location.href = '/cart';
      })
      .catch(function (error) {
        console.warn('Add to cart failed:', error);

        alert(
          error && error.description
            ? error.description
            : 'Could not add this product to cart. Please try again.'
        );

        if (button) {
          button.disabled = false;
          button.removeAttribute('aria-disabled');
        }

        if (buttonText) {
          buttonText.textContent = 'Add To Cart';
        }
      });
  }

  function initAddToCartProtection(scope) {
    var productData = getProductData(scope);
    var forms = Array.prototype.slice.call(scope.querySelectorAll('form[action*="/cart/add"]'));

    if (!forms.length) {
      return;
    }

    forms.forEach(function (form) {
      if (form.dataset.shAtcProtected === 'true') {
        return;
      }

      form.dataset.shAtcProtected = 'true';

      var variantInput = form.querySelector('input[name="id"]');
      var addButton =
        form.querySelector('[data-add-to-cart-button]') ||
        scope.querySelector('[data-add-to-cart-button]');

      var addButtonText =
        form.querySelector('[data-add-to-cart-text]') ||
        scope.querySelector('[data-add-to-cart-text]');

      form.addEventListener('submit', function (event) {
        event.preventDefault();

        if (!variantInput || !variantInput.value) {
          alert('Please select a product option first.');
          return;
        }

        if (productData) {
          var variant = getVariantById(productData, variantInput.value);

          if (!variant) {
            alert('Invalid product variant. Please select product option again.');
            return;
          }

          if (!variant.available) {
            alert('This selected variant is sold out. Please choose another option.');
            return;
          }
        }

        ajaxAddAndRedirect(form, addButton, addButtonText);
      });
    });
  }

  function initOrderSample(scope) {
    var sampleButton = scope.querySelector('[data-order-sample-button]');

    if (!sampleButton || sampleButton.dataset.initialized === 'true') {
      return;
    }

    sampleButton.dataset.initialized = 'true';

    sampleButton.addEventListener('click', function () {
      var sampleVariantId = sampleButton.getAttribute('data-sample-variant-id');

      if (!sampleVariantId) {
        alert('Sample product variant ID is missing. Please add sample_variant_id metafield.');
        return;
      }

      sampleButton.disabled = true;
      sampleButton.setAttribute('aria-disabled', 'true');

      fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: Number(sampleVariantId),
          quantity: 1
        })
      })
        .then(function (response) {
          if (!response.ok) {
            return response.json().then(function (data) {
              throw data;
            });
          }

          return response.json();
        })
        .then(function () {
          window.location.href = '/cart';
        })
        .catch(function (error) {
          console.warn('Order sample failed:', error);

          alert(
            error && error.description
              ? error.description
              : 'Could not add sample to cart. Please check sample variant ID.'
          );

          sampleButton.disabled = false;
          sampleButton.removeAttribute('aria-disabled');
        });
    });
  }

  function initGlobalProductCardAddToCart() {
    if (document.documentElement.dataset.shCardAtcInitialized === 'true') {
      return;
    }

    document.documentElement.dataset.shCardAtcInitialized = 'true';

    document.addEventListener('submit', function (event) {
      var form = event.target;

      if (!form || !form.matches('.sh-product-card__form')) {
        return;
      }

      event.preventDefault();

      ajaxAddAndRedirect(form, form.querySelector('button[type="submit"]'), null);
    });
  }

  function fallbackCopy(text) {
    var textarea = document.createElement('textarea');

    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    textarea.style.top = '-9999px';

    document.body.appendChild(textarea);

    textarea.select();

    try {
      document.execCommand('copy');
    } catch (error) {
      console.warn('Copy failed:', error);
    }

    document.body.removeChild(textarea);
  }

  function initCopyLink(scope) {
    var copyButton =
      scope.querySelector('[data-copy-current-product-link]') ||
      scope.querySelector('[data-copy-link]');

    if (!copyButton || copyButton.dataset.initialized === 'true') {
      return;
    }

    copyButton.dataset.initialized = 'true';

    copyButton.addEventListener('click', function () {
      var originalText = copyButton.textContent;

      function showCopied() {
        copyButton.textContent = 'Copied';

        window.setTimeout(function () {
          copyButton.textContent = originalText || 'Copy Link';
        }, 1600);
      }

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard
          .writeText(window.location.href)
          .then(showCopied)
          .catch(function () {
            fallbackCopy(window.location.href);
            showCopied();
          });
      } else {
        fallbackCopy(window.location.href);
        showCopied();
      }
    });
  }

  function initPrintButton(scope) {
    var printButton =
      scope.querySelector('[data-print-product-page]') ||
      scope.querySelector('[data-print-button]');

    if (!printButton || printButton.dataset.initialized === 'true') {
      return;
    }

    printButton.dataset.initialized = 'true';

    printButton.addEventListener('click', function () {
      window.print();
    });
  }

  function initFulfillmentCards(scope) {
    Array.prototype.slice.call(scope.querySelectorAll('[data-fulfillment-card]')).forEach(function (card) {
      if (card.dataset.initialized === 'true') {
        return;
      }

      card.dataset.initialized = 'true';

      card.addEventListener('click', function () {
        Array.prototype.slice.call(scope.querySelectorAll('[data-fulfillment-card]')).forEach(function (item) {
          item.classList.remove('is-active');
        });

        card.classList.add('is-active');
      });
    });
  }

  function initSpecificationToggle() {
    Array.prototype.slice.call(document.querySelectorAll('[data-spec-toggle]')).forEach(function (button) {
      if (button.dataset.initialized === 'true') {
        return;
      }

      button.dataset.initialized = 'true';

      button.addEventListener('click', function () {
        var section = button.closest('.sh-product-specifications');

        if (!section) {
          return;
        }

        var content = section.querySelector('[data-spec-content]');
        var arrow = section.querySelector('.sh-product-specifications__arrow');

        if (!content) {
          return;
        }

        var isHidden = content.hidden;

        content.hidden = !isHidden;
        button.setAttribute('aria-expanded', isHidden ? 'true' : 'false');

        if (arrow) {
          arrow.classList.toggle('is-open', isHidden);
        }
      });
    });
  }

  function initProductAccordion() {
    Array.prototype.slice.call(document.querySelectorAll('[data-product-accordion-toggle]')).forEach(function (button) {
      if (button.dataset.initialized === 'true') {
        return;
      }

      button.dataset.initialized = 'true';

      button.addEventListener('click', function () {
        var item = button.closest('.sh-product-accordion__item');

        if (!item) {
          return;
        }

        var content = item.querySelector('[data-product-accordion-content]');
        var arrow = item.querySelector('.sh-product-accordion__arrow');

        if (!content) {
          return;
        }

        var isHidden = content.hidden;

        content.hidden = !isHidden;
        button.setAttribute('aria-expanded', isHidden ? 'true' : 'false');

        if (arrow) {
          arrow.classList.toggle('is-open', isHidden);
        }
      });
    });
  }

  function initAll() {
    document.querySelectorAll('.sh-product-flooring').forEach(function (section) {
      initProductGallery(section);
      initFlooringCalculator(section);
      initVariantPicker(section);
      initAddToCartProtection(section);
      initOrderSample(section);
      initCopyLink(section);
      initPrintButton(section);
      initFulfillmentCards(section);
    });

    initSpecificationToggle();
    initProductAccordion();
  }

  document.addEventListener('DOMContentLoaded', function () {
    initAll();
    initGlobalProductCardAddToCart();
  });

  document.addEventListener('shopify:section:load', function () {
    initAll();
  });
})();